import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Navigate } from 'react-router-dom';
import { z } from 'zod';
import { useAuth } from '@/app/AuthContext';
import { AuthLayout } from '@/layouts/AuthLayout';
import { Button } from '@/components/Button';
import { ApiRequestError } from '@/services/api';
import { securityService } from '@/services/security.service';
import { hasPlatformAuthenticator, passkeyMethodLabel, PasskeyCancelled } from '@/utils/webauthn';
import { v } from '@/theme/tokens';

const loginSchema = z.object({
  email: z.string().min(1, 'Укажите email').email('Некорректный email'),
  password: z.string().min(1, 'Укажите пароль'),
});

type LoginForm = z.infer<typeof loginSchema>;

/**
 * Whether to offer the passkey button at all.
 *
 * Three things have to be true, and all three are asked rather than assumed:
 * the browser has the API, this device has a built-in authenticator, and the
 * server has a resolvable RP ID. A button that cannot work is worse than no
 * button — it teaches people the feature is broken.
 */
function usePasskeyAvailability() {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [platform, config] = await Promise.all([
          hasPlatformAuthenticator(),
          securityService.passkeyConfig(),
        ]);
        if (!cancelled) setAvailable(platform && config.enabled);
      } catch {
        if (!cancelled) setAvailable(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return available;
}

export function LoginPage() {
  const { user, login, loginWithPasskey } = useAuth();
  const [formError, setFormError] = useState<string | null>(null);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const passkeyAvailable = usePasskeyAvailability();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({ resolver: zodResolver(loginSchema) });

  if (user) return <Navigate to="/" replace />;

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await login(values.email, values.password);
      // Offer the passkey once, on the next screen, and only to a browser that
      // has not been asked before. See SecuritySection.
      window.sessionStorage.setItem('ramenten-passkey-hint', 'pending');
    } catch (error) {
      if (error instanceof ApiRequestError) {
        setFormError(
          error.status === 429
            ? 'Слишком много попыток входа. Попробуйте позже.'
            : error.message,
        );
      } else {
        setFormError('Не удалось выполнить вход');
      }
    }
  });

  const onPasskey = async () => {
    setFormError(null);
    setPasskeyBusy(true);
    try {
      await loginWithPasskey();
    } catch (error) {
      // Dismissing the Face ID sheet is not an error; say nothing.
      if (error instanceof PasskeyCancelled) return;
      setFormError(
        error instanceof ApiRequestError
          ? error.message
          : 'Не удалось войти с помощью passkey. Войдите по паролю.',
      );
    } finally {
      setPasskeyBusy(false);
    }
  };

  const field = (invalid: boolean) => ({
    width: '100%',
    padding: '11px 13px',
    border: `1px solid ${invalid ? v('negative') : v('line')}`,
    borderRadius: 10,
    margin: '6px 0 4px',
    fontSize: 14,
    fontFamily: 'inherit',
    background: v('surface'),
    color: v('ink'),
  });

  return (
    <AuthLayout title="Вход в систему">
      <form onSubmit={onSubmit} noValidate>
        <label htmlFor="email" style={{ fontSize: 12.5, color: v('ink-sub'), fontWeight: 600 }}>
          Email
        </label>
        <input
          id="email"
          type="email"
          autoComplete="username webauthn"
          {...register('email')}
          style={field(Boolean(errors.email))}
        />
        {errors.email && (
          <div style={{ color: v('negative'), fontSize: 12, marginBottom: 8 }}>
            {errors.email.message}
          </div>
        )}

        <label
          htmlFor="password"
          style={{ fontSize: 12.5, color: v('ink-sub'), fontWeight: 600, marginTop: 10, display: 'block' }}
        >
          Пароль
        </label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          {...register('password')}
          style={field(Boolean(errors.password))}
        />
        {errors.password && (
          <div style={{ color: v('negative'), fontSize: 12, marginBottom: 8 }}>
            {errors.password.message}
          </div>
        )}

        {formError && (
          <div
            role="alert"
            style={{
              background: v('negative-soft'),
              color: v('negative'),
              padding: '10px 12px',
              borderRadius: 10,
              fontSize: 13,
              margin: '12px 0',
            }}
          >
            {formError}
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          disabled={isSubmitting}
          style={{ width: '100%', justifyContent: 'center', padding: 12, marginTop: 16 }}
        >
          {isSubmitting ? 'Вход…' : 'Войти'}
        </Button>
      </form>

      {passkeyAvailable && (
        <>
          <div
            aria-hidden
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              margin: '18px 0 14px',
              color: v('ink-muted'),
              fontSize: 12,
            }}
          >
            <span style={{ flex: 1, height: 1, background: v('line') }} />
            или
            <span style={{ flex: 1, height: 1, background: v('line') }} />
          </div>

          <Button
            type="button"
            variant="ghost"
            onClick={onPasskey}
            disabled={passkeyBusy}
            style={{ width: '100%', justifyContent: 'center', padding: 12 }}
          >
            {passkeyBusy ? 'Ожидание подтверждения…' : `Войти с ${passkeyMethodLabel()}`}
          </Button>
        </>
      )}

      <p style={{ fontSize: 12, color: v('ink-muted'), textAlign: 'center', marginTop: 16 }}>
        Доступ выдаётся по приглашению. Самостоятельная регистрация не предусмотрена.
      </p>
    </AuthLayout>
  );
}
